/**
 * BibleHome - Enhanced Bible landing page with dashboard
 * Features: Today's verse, active plans, progress, quick navigation
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/lib/store';

// UI Components
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';

// Icons
import { BookOpen, LayoutDashboard, Calendar, TrendingUp, PenLine, ChevronLeft } from 'lucide-react';

// New Components
import BibleDashboard from '@/components/bible/BibleDashboard';
import EnhancedBibleReader from '@/components/bible/EnhancedBibleReader';
import ReadingPlanViewer from '@/components/bible/ReadingPlanViewer';
import MyBibleLibrary from '@/components/bible/MyBibleLibrary';
import BibleProgress from '@/components/bible/BibleProgress';

const BibleHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  
  const [activeTab, setActiveTab] = useState('home');
  const [viewingPlan, setViewingPlan] = useState(null);

  // Check for plan navigation from state
  React.useEffect(() => {
    if (location.state?.planId) {
      setViewingPlan(location.state.planId);
      setActiveTab('plans');
    }
  }, [location.state]);

  // If viewing a specific plan
  if (viewingPlan) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto p-4 sm:p-6">
          <ReadingPlanViewer 
            userPlanId={viewingPlan} 
            onBack={() => setViewingPlan(null)} 
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-indigo-600" />
                <span className="text-xl font-bold text-slate-900">Bible</span>
              </div>
            </div>
            
            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600 hidden sm:inline">
                  Welcome, {user?.name?.split(' ')[0]}
                </span>
              </div>
            ) : (
              <Button onClick={() => navigate('/login')} size="sm">
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 lg:w-auto lg:inline-flex">
            <TabsTrigger value="home" className="gap-2">
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden sm:inline">Home</span>
            </TabsTrigger>
            <TabsTrigger value="read" className="gap-2">
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Read</span>
            </TabsTrigger>
            <TabsTrigger value="plans" className="gap-2">
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">Plans</span>
            </TabsTrigger>
            {isAuthenticated && (
              <>
                <TabsTrigger value="progress" className="gap-2">
                  <TrendingUp className="h-4 w-4" />
                  <span className="hidden sm:inline">Progress</span>
                </TabsTrigger>
                <TabsTrigger value="library" className="gap-2">
                  <PenLine className="h-4 w-4" />
                  <span className="hidden sm:inline">Notes</span>
                </TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="home" className="mt-6">
            <BibleDashboard />
          </TabsContent>

          <TabsContent value="read" className="mt-6">
            <EnhancedBibleReader />
          </TabsContent>

          <TabsContent value="plans" className="mt-6">
            <PlansSection 
              onViewPlan={(planId) => setViewingPlan(planId)}
              isAuthenticated={isAuthenticated}
              navigate={navigate}
            />
          </TabsContent>

          {isAuthenticated && (
            <>
              <TabsContent value="progress" className="mt-6">
                <BibleProgress />
              </TabsContent>

              <TabsContent value="library" className="mt-6">
                <MyBibleLibrary />
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>
    </div>
  );
};

// ============== Plans Section ==============
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { BookMarked, Users, Play, ChevronRight, Trophy, Clock, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import AssignedPlansSection from '@/components/bible/AssignedPlansSection';

const PlansSection = ({ onViewPlan, isAuthenticated, navigate }) => {
  const queryClient = useQueryClient();

  // Fetch available plans
  const { data: plans = [] } = useQuery({
    queryKey: ['bible-plans'],
    queryFn: () => api.get('/bible/plans').then(r => r.data),
  });

  // Fetch user's active plans
  const { data: myPlans = [] } = useQuery({
    queryKey: ['my-plans'],
    queryFn: () => api.get('/bible/my-plans').then(r => r.data),
    enabled: isAuthenticated,
  });

  // Start plan mutation
  const startPlanMutation = useMutation({
    mutationFn: (planId) => api.post(`/bible/plans/${planId}/start`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries(['my-plans']);
      toast.success('Reading plan started!');
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to start plan');
    },
  });

  const handleStartPlan = (planId) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to start a reading plan');
      navigate('/login');
      return;
    }
    startPlanMutation.mutate(planId);
  };

  const activePlans = myPlans.filter(p => p.status === 'active');

  return (
    <div className="space-y-8">
      {/* Plans set by a church, province or event — iter 582 */}
      {isAuthenticated && <AssignedPlansSection onViewPlan={onViewPlan} />}

      {/* "From your communities" — scope-aware reading plans */}
      {isAuthenticated && <CommunityPlansSection onViewPlan={onViewPlan} />}

      {/* My Active Plans */}
      {isAuthenticated && activePlans.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4">My Active Plans</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {activePlans.map((plan) => (
              <Card 
                key={plan.id}
                className="hover:shadow-lg transition-all cursor-pointer group overflow-hidden"
                onClick={() => onViewPlan(plan.id)}
              >
                <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-teal-500" style={{ width: `${plan.completion_percent || 0}%` }} />
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white shadow-md flex-shrink-0">
                      <Calendar className="h-6 w-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{plan.plan_title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-slate-500">Day {plan.current_day} of {plan.duration_days}</span>
                        <Badge variant="outline" className="text-xs">{Math.round(plan.completion_percent || 0)}%</Badge>
                      </div>
                      <Progress value={plan.completion_percent || 0} className="h-1.5 mt-3" />
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Available Plans */}
      <div>
        <h2 className="text-xl font-semibold text-slate-900 mb-4">
          {activePlans.length > 0 ? 'Explore More Plans' : 'Reading Plans'}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => {
            const isSubscribed = myPlans.some(mp => mp.plan_id === plan.id);
            
            return (
              <Card key={plan.id} className="hover:shadow-lg transition-all overflow-hidden group">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <BookMarked className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">
                        {plan.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="secondary" className="text-xs">{plan.duration_days} days</Badge>
                        {plan.difficulty && (
                          <Badge variant="outline" className="text-xs capitalize">{plan.difficulty}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-slate-600 line-clamp-2 mb-4">{plan.description}</p>
                  
                  <div className="flex items-center justify-between">
                    {plan.subscribers_count > 0 && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Users className="h-3 w-3" /> {plan.subscribers_count} reading
                      </span>
                    )}
                    
                    {isSubscribed ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Joined
                      </Badge>
                    ) : (
                      <Button 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartPlan(plan.id);
                        }}
                        disabled={startPlanMutation.isPending}
                      >
                        <Play className="h-3 w-3 mr-1" /> Start
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {plans.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <BookMarked className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-medium text-slate-600 mb-2">No Reading Plans Available</h3>
              <p className="text-sm text-slate-500">
                Check back soon for new reading plans!
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};


// ============== Community-scoped plans ("From your school / church / events") ==============
const CommunityPlansSection = ({ onViewPlan }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['my-scoped-bible-plans'],
    queryFn: () => api.get('/bible/my-scoped-plans').then((r) => r.data),
  });
  const startMutation = useMutation({
    mutationFn: (planId) => api.post(`/bible/plans/${planId}/start`, { reading_plan_id: planId }),
    onSuccess: () => { toast.success('Plan started — happy reading!'); queryClient.invalidateQueries({ queryKey: ['my-scoped-bible-plans'] }); queryClient.invalidateQueries({ queryKey: ['my-plans'] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to start'),
  });

  if (isLoading) return null;
  const sections = data?.sections || [];
  if (sections.length === 0) return null;

  const scopeLabel = (t) => ({ school: 'School', church: 'Church', event: 'Event' })[t] || t;

  return (
    <div data-testid="community-plans-section">
      <h2 className="text-xl font-semibold text-slate-900 mb-1">From your communities</h2>
      <p className="text-sm text-slate-500 mb-4">Reading journeys curated by the schools, churches and events you belong to.</p>
      <div className="space-y-5">
        {sections.map((sec) => (
          <div key={`${sec.scope_type}-${sec.scope_id}`} data-testid={`community-section-${sec.scope_type}-${sec.scope_id}`}>
            <div className="flex items-center gap-2 mb-2">
              {sec.tenant_logo ? (
                <img src={sec.tenant_logo} alt="" className="w-7 h-7 rounded object-cover" />
              ) : (
                <div className="w-7 h-7 rounded bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-bold">
                  {(sec.tenant_name || '?').charAt(0)}
                </div>
              )}
              <span className="font-semibold text-sm">{sec.tenant_name}</span>
              <Badge variant="outline" className="text-[10px] uppercase">{scopeLabel(sec.scope_type)}</Badge>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {sec.plans.map((p) => {
                const prog = p.progress;
                return (
                  <Card key={p.id} className="hover:shadow-md transition-all cursor-pointer group overflow-hidden" data-testid={`community-plan-card-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3 mb-2">
                        <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center"><BookMarked className="h-4 w-4 text-indigo-600" /></div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm truncate group-hover:text-indigo-600">{p.title}</h3>
                          <div className="text-xs text-slate-500">{p.duration_days} days · {p.category || 'general'}</div>
                        </div>
                      </div>
                      {prog ? (
                        <>
                          <Progress value={prog.completion_percent} className="h-1.5 mb-2" />
                          <Button size="sm" variant="outline" className="w-full" onClick={() => onViewPlan(prog.user_plan_id)} data-testid={`community-plan-continue-${p.id}`}>
                            <Play className="h-3.5 w-3.5 mr-1" /> Continue · Day {prog.current_day}
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" className="w-full" disabled={startMutation.isPending} onClick={() => startMutation.mutate(p.id)} data-testid={`community-plan-start-${p.id}`}>
                          <Play className="h-3.5 w-3.5 mr-1" /> Start plan
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BibleHome;
